import { camelCase, get } from 'lodash';
import type { CmsPageCollectionItems } from '$cms/types';

/**
 * Replaces placeholders with the corresponding collection field value.
 *
 * Placeholders: {area}, {product}, {car-make}, {car-model}, {symptom}.
 * The default placeholder resolves the collection name, but it can be extended to resolve any "displayable" field value.
 * eg. {symptom.subTitle}
 *
 * @param textWithPlaceholders a string containing collection placeholders
 * @param collections CmsPageCollectionItems which should contain the collections used in placeholder
 * @param field optional field to display from the collection item
 * @returns string
 * @throws TypeError when a placeholder is not found in the collections object or the field is not displayable
 */
export function replaceCollectionPlaceholders(
  textWithPlaceholders: string,
  collections: CmsPageCollectionItems = {},
  field?: string
): string {
  let text = textWithPlaceholders;
  const placeholderMatchIt = textWithPlaceholders.matchAll(/\{([A-z-.]+)\}/g);

  for (const [placeholder, placeholderInner] of placeholderMatchIt) {
    const collectionPath = placeholderInner.split('.').map((v) => camelCase(v));
    if (collectionPath.length === 1) {
      // default placeholders read from field `name`
      collectionPath.push(field ?? 'name');
    }

    const val = get(collections, collectionPath);

    /**
     * @note Only string and number fields are displayable for now.
     */
    if (!val || (typeof val !== 'string' && typeof val !== 'number')) {
      throw new TypeError(
        `Collection placeholder "${placeholder}" not displayable in text: "${textWithPlaceholders}"`
      );
    }

    text = text.replace(placeholder, val.toString());
  }

  return text;
}
